/**
 * 📍 Dashboard Route Map & Access Control
 *
 * This file builds the list of available routes in the Dashboard based on:
 * - User permissions
 * - Feature flags from license
 * - Persona type (analytics, admin, marketing, security)
 *
 * -------------------------------
 * 🛠 To add a new dashboard route:
 * 1. Add the route to `routes.ts` (defines the path).
 * 2. Create or import the component (e.g., via lazy).
 * 3. Add a new object to the `rawRoutes` array:
 *    - Set: `key`, `path`, `element`, `persona`, `featureKey`, `hasPermission`.
 * 4. (Optional) Add to sidebar in `UserData.tsx` for visual navigation.
 *
 * 🔐 `getFeatureRouteElement(...)` wraps each route:
 *    - Renders `StatusScreen` if locked (feature disabled).
 *    - Redirects to `/404` if permission is missing.
 */
import { lazy, ReactNode, Suspense } from "react"
import { Navigate } from "react-router-dom"

import { routes } from "../../../routes/routes"
import { RouteConfig } from "../../../types/Custom/Interfaces"
import { FeatureMetaKey } from "../../../types/Custom/Types"
import { definitions } from "../../../types/Generated/apiTypes"
import Auth from "../../../utils/auth"
import { featuresMeta } from "../../../utils/featureMeta"
import CampaignContextProvider from "../../CampaignPlanner/CampaignContext/CampaignContext"
import FloorsContextProvider from "../../Floors/floorsContext/FloorsContext"
import StatusScreen from "../../StatusScreens/StatusScreen"
import ScreenSkeleton from "./ScreenSkeleton"

// Lazy load components

// Multiple  Components
const BranchesGatesAnalytics = lazy(() => import("../../MultipleBranches/GatesAnalytics/GatesAnalytics"))
const ConnectivityView = lazy(() => import("../../MultipleBranches/Connectivity/ConnectivityView"))
const CategoriesMultipleBranches = lazy(() => import("../../MultipleBranches/Categories/Categories"))

// Analytics Components
const EntranceGates = lazy(() => import("../../EntranceGates/EntranceGates"))
const CounterGates = lazy(() => import("../../EntranceGates/components/CounterGates"))
const Categories = lazy(() => import("../../Categories/Categories"))
const TenantsList = lazy(() => import("../../TenantsList/TenantsList"))
const DetailsPage = lazy(() => import("../../TenantsList/DetailsPage/DetailsPage"))
const DwellingAreas = lazy(() => import("../../DwellingAreas/DwellingAreas"))
const DwellingArea = lazy(() => import("../../DwellingAreas/DetailsPage/DwellingArea"))
const CheckoutCounter = lazy(() => import("../../Cashiers/CheckoutCounter"))
const Corridors = lazy(() => import("../../Corridors/Corridors"))
const CustomerService = lazy(() => import("../../CustomerService/CustomerService"))
const MissingDataList = lazy(() => import("../../MissingData/MissingDataList"))
const Occupancy = lazy(() => import("../../GenericOccupancy/GenericOccupancy"))
const Heatmap = lazy(() => import("../../Heatmap/Heatmap"))
const OldHeatmap = lazy(() => import("../../OldHeatmap/Heatmap"))
const FloorsMCP = lazy(() => import("../../MCP/FloorsMCP/FloorsMCP"))
const CamerasMCP = lazy(() => import("../../MCP/CamerasMCP/CamerasMCP"))
const CarsAnalytics = lazy(() => import("../../Cars/CarsAnalytics/CarsAnalytics"))

// Marketing Components
const CampaignPlanner = lazy(() => import("../../CampaignPlanner/CampaignPlanner"))
const CampaignDetaiLsWrapper = lazy(() => import("../../CampaignPlanner/CampaignDetails/CampaignDetaiLsWrapper"))

// Security Components
const LiveStreams = lazy(() => import("../../LiveStreams/LiveStreams"))
const SecurityEvents = lazy(() => import("../../SecurityEvents/SecurityEvents"))
const CarsHistory = lazy(() => import("../../Cars/CarsHistory/CarsHistory"))
const FaceRecognition = lazy(() => import("../../FaceRecognition/FaceRecognition"))
const ProfilePaths = lazy(() => import("../../FaceRecognition/ProfilePaths/ProfilePaths"))

// Admin Components
const CamerasList = lazy(() => import("../../Cameras/CamerasList"))
const CameraDetails = lazy(() => import("../../Cameras/CameraDetails/CameraDetails"))
const CVC = lazy(() => import("../../CVC/CVC"))
const FloorsList = lazy(() => import("../../Floors/FloorsList"))
const EntitiesAndRegions = lazy(() => import("../../EntitiesAndRegions/EntitiesAndRegions"))
const IntrusionSetupList = lazy(() => import("../../SecurityServiceSetup/Intrusion/IntrusionSetupList"))
const MotionSetupList = lazy(() => import("../../SecurityServiceSetup/Motion/MotionSetupList"))
const NodesList = lazy(() => import("../../Nodes/NodesList"))
const NodesDownAnalytics = lazy(() => import("../../Nodes/DownAnalytics/NodesDownAnalytics"))
const CategoriesList = lazy(() => import("../../CategoriesManagement/CategoriesList"))
const AllUsers = lazy(() => import("../../AdminPanel/AllUsers"))
const UserAddEdit = lazy(() => import("../../AdminPanel/UserAddEdit"))
const CarsListsScreen = lazy(() => import("../../Cars/CarsLists/CarsListsScreen"))
const APIIntegration = lazy(() => import("../../APIIntegration/APIIntegration"))
const SynapseLicense = lazy(() => import("../../SynapseLicense/SynapseLicense"))
const DefectDetectionSetup = lazy(() => import("../../DefectDetectionSetup/DefectDetectionSetupList"))

// Industrial Components
const DefectDetectionAnalytics = lazy(() => import("../../DefectDetectionAnalytics/DefectDetectionAnalytics"))

// Common Components
const PageNotFound = lazy(() => import("../../StatusScreens/NotFound/NotFound"))
const NotAllowed = lazy(() => import("../../StatusScreens/NotAllowed/NotAllowed"))

type Features = definitions["Features"]

/**
 * Renders a route element with proper feature checks and permissions
 * @param {RouteConfig} route - The route configuration object
 * @param {Features | null} features - The features object containing feature flags
 * @returns {ReactNode} The rendered route element with appropriate checks
 */
export const getFeatureRouteElement = (route: RouteConfig, features: Features | null): ReactNode => {
  const { key, persona, featureKey, hasPermission, element } = route

  const renderWithSuspense = (element: ReactNode) => <Suspense fallback={<ScreenSkeleton />}>{element}</Suspense>

  // Check if persona and featureKey are provided; if not, render without restrictions (routes like 404 or no permissions,etc..)
  if (!persona || !featureKey) {
    return renderWithSuspense(element)
  }

  // license feature schema doesn't include admin screens (depend only on permissions)
  if (persona === "admin") {
    // For admin persona, only check permissions, not feature flags
    return hasPermission ? renderWithSuspense(element) : <Navigate to={`/${routes[404]}`} />
  }

  // Get the features object for the current persona (analytics, marketing, security)
  const personaFeatures = features?.[persona] ?? null

  // If no features are defined for this persona, redirect to 404
  if (personaFeatures === null) {
    return <Navigate to={`/${routes[404]}`} />
  }

  // Check if the specific feature is active for this persona
  // Type assertion is safe here because featureKey is always valid for the given persona
  const isFeatureActive = personaFeatures[featureKey as keyof typeof personaFeatures] ?? null

  // If the feature is not active, show a lock/status screen or nothing
  if (!isFeatureActive) {
    // If the route key matches a known feature meta, show a lock screen with contact button
    if (key && Object.keys(featuresMeta).includes(key)) {
      const statusProps = featuresMeta[key as FeatureMetaKey]
      return (
        <StatusScreen
          {...statusProps}
          isLockScreen
          buttonText="Contact synapse team"
          buttonAction={() => window.open("https://azkavision.synapse-analytics.io/contact/", "_blank")}
        />
      )
    }
    // Otherwise, render nothing
    return null
  }

  // If the user doesn't have permission, redirect to 404
  if (!hasPermission) {
    return <Navigate to={`/${routes[404]}`} />
  }

  // All checks passed, render the route element
  return renderWithSuspense(element)
}

/**
 * Generates the complete dashboard routes configuration with feature checks
 * @param {Features | null} features - The features object containing feature flags
 * @param {string} [landingScreen] - Optional landing screen path
 * @returns {RouteConfig[]} Array of route configurations with feature checks applied
 */
export const getDashboardRoutes = (features: Features | null, landingScreen?: string): RouteConfig[] => {
  const rawRoutes: RouteConfig[] = [
    // Default routes
    {
      key: "catch-all",
      path: "*",
      element: <Navigate to={`/${routes?.[404]}`} />,
      hasPermission: true,
    },
    {
      key: "root",
      path: "/",
      element: <Navigate to={landingScreen ?? ""} replace />,
      hasPermission: true,
    },
    {
      key: "404",
      path: `/${routes?.[404]}`,
      element: <PageNotFound />,
      hasPermission: true,
    },
    {
      key: "no-permissions",
      path: `/${routes?.noPermissions}`,
      element: <NotAllowed />,
      hasPermission: true,
    },
    // Analytics routes
    {
      key: "gates",
      path: `/${routes?.gates}/*`,
      element: <EntranceGates />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
      children: [
        {
          key: "gates-live",
          path: `${routes?.liveGates}`,
          element: <CounterGates isLive />,
          hasPermission: Auth.hasFeaturePermission("people_count"),
          persona: "analytics",
          featureKey: "counter",
        },
      ],
    },
    {
      key: "gates-redirect",
      path: `/${routes?.gates}`,
      element: <Navigate to={`${routes?.liveGates}`} replace />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
    },
    {
      key: "categories",
      path: `/${routes?.categories}`,
      element: <Categories />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "tenants",
      path: `/${routes?.tenantsList}`,
      element: <TenantsList />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "tenants",
      path: `/${routes?.tenantsList}/${routes?.tenantDetails}/:id`,
      element: <DetailsPage />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "dwelling",
      path: `/${routes?.dwellingAreas}`,
      element: <DwellingAreas />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "dwelling",
      path: `/${routes?.dwellingAreas}/${routes?.dwellingAreaDetails}/:id`,
      element: <DwellingArea />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "checkout",
      path: `/${routes?.checkoutCounter}`,
      element: <CheckoutCounter />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "corridors",
      path: `/${routes?.corridors}`,
      element: <Corridors />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "customer-service",
      path: `/${routes?.customerService}`,
      element: <CustomerService />,
      hasPermission: Auth.hasFeaturePermission("people_count"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "missing_data",
      path: `/${routes?.missingData}`,
      element: <MissingDataList />,
      hasPermission: Auth.hasFeaturePermission("missing_data"),
      persona: "analytics",
      featureKey: "counter",
    },
    {
      key: "staff_occupancy",
      path: `/${routes?.staffOccupancy}`,
      element: <Occupancy occupancyType="Staff" />,
      hasPermission: Auth.hasFeaturePermission("zones_occupancy"),
      persona: "analytics",
      featureKey: "occupancy",
    },
    {
      key: "seat_occupancy",
      path: `/${routes?.seatOccupancy}`,
      element: <Occupancy occupancyType="Seat" />,
      hasPermission: Auth.hasFeaturePermission("zones_occupancy"),
      persona: "analytics",
      featureKey: "occupancy",
    },
    {
      key: "heatmap",
      path: `/${routes?.heatmap}`,
      element: <Heatmap />,
      hasPermission: Auth.hasFeaturePermission("heatmap"),
      persona: "analytics",
      featureKey: "heatmap",
    },
    {
      key: "old_heatmap",
      path: `/${routes?.oldHeatmap}`,
      element: <OldHeatmap />,
      hasPermission: Auth.hasFeaturePermission("heatmap"),
      persona: "analytics",
      featureKey: "heatmap",
    },
    {
      key: "floors_mcp",
      path: `/${routes?.floorsMCP}`,
      element: <FloorsMCP />,
      hasPermission: Auth.hasFeaturePermission("mcp"),
      persona: "analytics",
      featureKey: "mcp",
    },
    {
      key: "cameras_mcp",
      path: `/${routes?.camerasMCP}`,
      element: <CamerasMCP />,
      hasPermission: Auth.hasFeaturePermission("mcp"),
      persona: "analytics",
      featureKey: "mcp",
    },
    {
      key: "cars_analytics",
      path: `/${routes?.carsAnalytics}`,
      element: <CarsAnalytics />,
      hasPermission: Auth.hasFeaturePermission("cars_analytics"),
      persona: "analytics",
      featureKey: "cars",
    },
    {
      key: "multiple-gates-analytics",
      path: `/${routes?.branchesGatesAnalytics}`,
      element: <BranchesGatesAnalytics />,
      hasPermission: Auth.hasFeaturePermission("people_count") && Auth.hasFeaturePermission("multiple_branches"),
      featureKey: "counter",
    },
    {
      key: "multiple-cameras-connectivity",
      path: `/${routes?.branchesCamerasConnectivity}`,
      element: <ConnectivityView entityType="Camera" showHealth />,
      hasPermission: Auth.hasFeaturePermission("cameras") && Auth.hasFeaturePermission("multiple_branches"),
    },
    {
      key: "multiple-nodes-connectivity",
      path: `/${routes?.branchesNodesConnectivity}`,
      element: <ConnectivityView entityType="Node" />,
      hasPermission: Auth.hasFeaturePermission("nodes") && Auth.hasFeaturePermission("multiple_branches"),
    },
    {
      key: "multiple-categories",
      path: `/${routes?.branchesCategories}`,
      element: <CategoriesMultipleBranches />,
      hasPermission: Auth.hasFeaturePermission("categories") && Auth.hasFeaturePermission("multiple_branches"),
    },
    // Marketing Routes
    {
      key: "campaign-planner",
      path: `/${routes?.campaign}`,
      element: (
        <CampaignContextProvider>
          <CampaignPlanner />
        </CampaignContextProvider>
      ),
      hasPermission: true,
      persona: "marketing",
      featureKey: "campaign",
    },
    {
      key: "campaign",
      path: `/${routes?.campaign}/:id`,
      element: (
        <CampaignContextProvider>
          <CampaignDetaiLsWrapper />
        </CampaignContextProvider>
      ),
      hasPermission: Auth.hasFeaturePermission("campaign"),
      persona: "marketing",
      featureKey: "campaign",
    },
    // Security Routes
    {
      key: "security_camera_stream",
      path: `/${routes?.liveStreams}`,
      element: <LiveStreams />,
      hasPermission: Auth.hasFeaturePermission("security_camera_stream"),
      persona: "security",
    },
    {
      key: "security_events",
      path: `/${routes?.securityIncidents}`,
      element: <SecurityEvents />,
      hasPermission: Auth.hasFeaturePermission("security_events"),
      persona: "security",
    },
    {
      key: "cars_security",
      path: `/${routes?.carsLogs}`,
      element: <CarsHistory />,
      hasPermission: Auth.hasFeaturePermission("cars_security"),
      persona: "security",
      featureKey: "cars",
    },
    {
      key: "faces",
      path: `/${routes?.faceRecognition}`,
      element: <FaceRecognition />,
      hasPermission: Auth.hasFeaturePermission("facial_recognition"),
      persona: "security",
      featureKey: "face",
    },
    {
      key: "profile_visits",
      path: `/${routes?.faceRecognition}/${routes?.profileVisits}/:id`,
      element: <ProfilePaths />,
      hasPermission: Auth.hasFeaturePermission("facial_recognition"),
      persona: "security",
      featureKey: "face",
    },
    // Admin Routes
    {
      key: "cameras-list",
      path: `/${routes?.cameraList}`,
      element: <CamerasList />,
      hasPermission: Auth.hasFeaturePermission("cameras"),
      persona: "admin",
    },
    {
      key: "camera-details",
      path: `/${routes?.cameraDetails}/:id`,
      element: <CameraDetails />,
      hasPermission: Auth.hasFeaturePermission("cameras"),
      persona: "admin",
    },
    {
      key: "camera-view-changes",
      path: `/${routes?.cameraViewChanges}`,
      element: <CVC />,
      hasPermission: Auth.hasFeaturePermission("cameras"),
      persona: "admin",
    },
    {
      key: "floors-list",
      path: `/${routes?.floors}`,
      element: (
        <FloorsContextProvider>
          <FloorsList />
        </FloorsContextProvider>
      ),
      hasPermission: Auth.hasFeaturePermission("floor"),
      persona: "admin",
    },
    {
      key: "entities-and-regions",
      path: `/${routes?.entitiesAndRegions}`,
      element: <EntitiesAndRegions />,
      hasPermission: Auth.hasFeaturePermission("entities"),
      persona: "admin",
    },
    {
      key: "intrusion-setup",
      path: `/${routes?.intrusionSetup}`,
      element: <IntrusionSetupList />,
      hasPermission: Auth.hasFeaturePermission("intrusion_setup"),
      persona: "admin",
    },
    {
      key: "motion-setup",
      path: `/${routes?.motionSetup}`,
      element: <MotionSetupList />,
      hasPermission: Auth.hasFeaturePermission("motion_setup"),
      persona: "admin",
    },
    {
      key: "nodes",
      path: `/${routes?.nodesList}`,
      element: <NodesList />,
      hasPermission: Auth.hasFeaturePermission("nodes"),
      persona: "admin",
    },
    {
      key: "node-down-analytics",
      path: `/${routes?.nodeDownAnalytics}/:id`,
      element: <NodesDownAnalytics />,
      hasPermission: Auth.hasFeaturePermission("nodes"),
      persona: "admin",
    },
    {
      key: "categories-list",
      path: `/${routes?.categoriesList}`,
      element: <CategoriesList />,
      hasPermission: Auth.hasFeaturePermission("categories"),
      persona: "admin",
    },
    {
      key: "all-users",
      path: `/${routes?.users}`,
      element: <AllUsers />,
      hasPermission: Auth.hasFeaturePermission("users"),
      persona: "admin",
    },
    {
      key: "users-add",
      path: `/${routes?.createUser}`,
      element: <UserAddEdit />,
      hasPermission: Auth.hasFeaturePermission("users"),
      persona: "admin",
    },
    {
      key: "user-edit",
      path: `/${routes?.users}/:id`,
      element: <UserAddEdit />,
      hasPermission: Auth.hasFeaturePermission("users"),
      persona: "admin",
    },
    {
      key: "cars-lists",
      path: `/${routes?.carsList}`,
      element: <CarsListsScreen />,
      hasPermission: Auth.hasFeaturePermission("cars_admin"),
      persona: "admin",
    },
    {
      key: "api-integration",
      path: `/${routes?.apiIntegration}`,
      element: <APIIntegration />,
      hasPermission: Auth.hasFeaturePermission("api_integration"),
      persona: "admin",
    },
    {
      key: "synapse-license",
      path: `/${routes?.license}`,
      element: <SynapseLicense />,
      hasPermission: true,
      persona: "admin",
    },
    {
      key: "defect-detection",
      path: `/${routes?.defectDetectionSetup}`,
      element: <DefectDetectionSetup />,
      hasPermission: Auth.hasFeaturePermission("defect_detection_setup"),
      persona: "admin",
    },
    {
      key: "defect-detection-analytics",
      path: `/${routes?.defectDetection}`,
      element: <DefectDetectionAnalytics />,
      hasPermission: Auth.hasFeaturePermission("defect_detection"),
      persona: "industrial",
    },
  ]

  return rawRoutes.map((route) => ({
    ...route,
    element: getFeatureRouteElement(route, features),
  }))
}
